import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  buildIngredientImport,
  buildRecipeImport,
  detectarPlantilla,
  dimensionOf,
  INGREDIENT_EXAMPLE_FILE,
  leerPlantillaIngredientes,
  leerPlantillaRecetas,
  looksLikeHeader,
  matchIngredientHeaders,
  matchRecipeHeaders,
  normalizeText,
  PLANTILLA_ARCHIVOS,
  RECIPE_EXAMPLE_FILE,
  UNITS,
  ingredientGroupLabel,
  type Recipe,
  type PlantillaDetectada,
  type RecipeDraft,
  recipeDraftReady,
  type ImportFix,
  type ImportIssue,
  type IngredientCorrections,
  type IngredientField,
  type IngredientImportResult,
  type RecipeCorrections,
  type RecipeField,
  type RecipeImportResult,
  type RecipeRowCorrection,
  type IngredientRowCorrection,
  type Unit,
} from '@alexiarest/shared';
import { api } from '../api/client';
import { useImportIngredients, useImportRecipes, useIngredients, useRecipes, useSettings, useSuppliers } from '../api/hooks';
import { ApiError } from '../api/error';
import { decimalSeparator, formatMoney, formatNumber, formatQty, parseDecimal } from '../format';
import { RECIPE_KIND_LABELS, unitLabel } from '../labels';
import { cantidadCocina } from '../lib/cantidadCocina';
import { descargarArchivoPublico, readImportFileRaw } from '../importFile';
import '../pages/ingredientes.css';
import { Drawer } from './Drawer';
import { SmartSelect } from './Field';
import { useToast } from './Toast';

/**
 * Asistente de importacion del catalogo.
 *
 * Tres pasos, y el del medio es el importante: NADA se escribe hasta que el
 * usuario ha visto exactamente que se va a crear, que se va a actualizar y
 * que filas no hemos entendido. Importar 200 ingredientes a ciegas y tener
 * que deshacerlo a mano seria peor que teclearlos.
 *
 * Fase 7 (decidido con Alex el 10 oct): lo que se puede arreglar se arregla
 * AQUÍ, sin volver al Excel: «Sí, $12.50», la unidad con un toque,
 * «Actualizar su precio / Es otra, créala», «¿Es tu Crema ácida?» (y
 * «Recordar que…», que guarda un alias), «¿Son 180 g?» y «¿Es una
 * preparación?». La previa se recalcula con cada respuesta; lo que quede sin
 * arreglar sigue fuera, como antes.
 */

type Modo = 'ingredientes' | 'recetas';

const MAX_FILAS = 5000;

interface WizardProps {
  open: boolean;
  onClose: () => void;
  /** Qué se importa al abrir. Sin indicarlo: recetas desde Tu carta y Preparaciones, ingredientes en el resto. */
  modoInicial?: Modo;
  /** La opción del menú «Excel ▾» que se eligió: su tarjeta sale destacada. */
  destacar?: 'lista' | 'precios';
  /** «Cambiar precios de los que ya tengo»: descarga la lista actual (la da Ingredientes). */
  onDescargarLista?: () => void;
  /** «¿Tienes las facturas a mano?»: abre Subir factura. */
  onSubirFactura?: () => void;
  /**
   * «Descargar todas las fichas (PDF)» (Tu carta y Preparaciones): antes era un
   * botón suelto arriba, «Todas en PDF» / «Recetario (PDF)» (revisión del 12 oct 2026).
   */
  onDescargarFichas?: () => void;
}

export function ImportCatalogWizard({ open, ...props }: WizardProps): JSX.Element | null {
  if (!open) return null;
  return <Cuerpo {...props} />;
}

/** Lo leído del archivo: se guarda para poder recalcular la previa con cada arreglo. */
interface Fuente {
  datos: string[][];
  colsIng?: Array<IngredientField | undefined>;
  colsRec?: Array<RecipeField | undefined>;
  /**
   * Plantilla de Alex (oct 2026): las filas crudas y dónde está la cabecera.
   * Se relee con cada cambio del IVA de «¿Con IVA?».
   */
  plantilla?: { raw: string[][]; det: PlantillaDetectada };
}

function Cuerpo({ onClose, modoInicial, destacar = 'lista', onDescargarLista, onSubirFactura, onDescargarFichas }: Omit<WizardProps, 'open'>): JSX.Element {
  const { pathname } = useLocation();
  const toast = useToast();
  const qc = useQueryClient();
  const ingredientesQuery = useIngredients();
  const recetasQuery = useRecipes();
  const proveedoresQuery = useSuppliers();
  const importIngredientes = useImportIngredients();
  const importRecetas = useImportRecipes();

  const [modo, setModo] = useState<Modo>(modoInicial ?? (/^\/(carta|preparaciones)/.test(pathname) ? 'recetas' : 'ingredientes'));
  const [filasLeidas, setFilasLeidas] = useState(0);
  const [nombreFichero, setNombreFichero] = useState('');
  const [error, setError] = useState('');
  const [leyendo, setLeyendo] = useState(false);
  const [fuente, setFuente] = useState<Fuente | null>(null);
  const [corrIng, setCorrIng] = useState<IngredientCorrections>({});
  const [corrRec, setCorrRec] = useState<RecipeCorrections>({});
  /** «Recordar que…», por fila del Excel. Marcado salvo que lo quite. */
  const [noRecordar, setNoRecordar] = useState<Record<number, boolean>>({});
  // Tras importar se recuerdan los alias (otra llamada): el botón sigue apagado.
  const [cerrando, setCerrando] = useState(false);
  const [erroresGuardado, setErroresGuardado] = useState<Array<{ row?: number; name: string; message: string }>>([]);

  const settings = useSettings();
  /** «¿Con IVA?» = Sí: el IVA que se les quita. El de la cuenta, salvo que lo cambie. */
  const [ivaTecleado, setIvaTecleado] = useState<string | null>(null);
  const ivaPlantilla = Math.max(0, Math.min(100, Number((ivaTecleado ?? String(settings.data?.vatPct ?? 16)).replace(',', '.')) || 0));
  const plantillaIng = useMemo(() => (fuente?.plantilla && fuente.plantilla.det.kind === 'ingredientes'
    ? leerPlantillaIngredientes(fuente.plantilla.raw, fuente.plantilla.det, ivaPlantilla)
    : null), [fuente, ivaPlantilla]);
  const previaIng: IngredientImportResult | null = useMemo(() => {
    const datos = plantillaIng?.datos ?? fuente?.datos;
    const cols = plantillaIng?.cols ?? fuente?.colsIng;
    return datos && cols ? buildIngredientImport(datos, cols, ingredientesQuery.data ?? [], proveedoresQuery.data ?? [], corrIng, { decimal: decimalSeparator() === '.' ? '.' : ',' }) : null;
  }, [fuente, plantillaIng, corrIng, ingredientesQuery.data, proveedoresQuery.data]);
  const previaRec: RecipeImportResult | null = useMemo(() => (fuente?.colsRec
    ? buildRecipeImport(fuente.datos, fuente.colsRec, ingredientesQuery.data ?? [], recetasQuery.data ?? [], corrRec, { decimal: decimalSeparator() === '.' ? '.' : ',' })
    : null), [fuente, corrRec, ingredientesQuery.data, recetasQuery.data]);

  const limpiar = () => {
    setFuente(null);
    setCorrIng({});
    setCorrRec({});
    setNoRecordar({});
    setError('');
    setNombreFichero('');
    setErroresGuardado([]);
  };

  const cargar = async (file: File) => {
    limpiar();
    setLeyendo(true);
    try {
      const hoja = modo === 'ingredientes' ? 'Ingredientes' : 'Recetas';
      const crudas = await readImportFileRaw(file, hoja);
      /*
       * La plantilla de Alex se reconoce por la cabecera y se lee por
       * posición: sin emparejar columnas ni preguntar nada. Si es la del otro
       * modo (la de recetas subida en Ingredientes), se cambia de modo solo.
       */
      const det = detectarPlantilla(crudas);
      if (det?.kind === 'gastos') {
        setError('Este es el Excel de gastos. Súbelo en Gastos › Importar gastos.');
        return;
      }
      if (det) {
        const filasPlantilla = det.kind === 'ingredientes'
          ? leerPlantillaIngredientes(crudas, det, 0).filas
          : leerPlantillaRecetas(crudas, det).filas;
        if (filasPlantilla === 0) {
          setError(det.kind === 'ingredientes'
            ? 'La plantilla está vacía: escribe tus ingredientes a partir de la fila 9 y vuelve a subirla. Los ejemplos en gris no se suben.'
            : 'La plantilla está vacía: escribe tus recetas debajo de la línea verde y vuelve a subirla. Los ejemplos en gris no se suben.');
          return;
        }
        if (filasPlantilla > MAX_FILAS) {
          setError(`El archivo tiene ${filasPlantilla} filas. El máximo son ${MAX_FILAS}.`);
          return;
        }
        if (det.kind !== modo) setModo(det.kind);
        setNombreFichero(file.name);
        setFilasLeidas(filasPlantilla);
        if (det.kind === 'ingredientes') {
          setFuente({ datos: [], plantilla: { raw: crudas, det } });
        } else {
          const leida = leerPlantillaRecetas(crudas, det);
          setFuente({ datos: leida.datos, colsRec: leida.cols, plantilla: { raw: crudas, det } });
        }
        return;
      }

      const filas = crudas.filter((row) => row.some((cell) => cell !== ''));
      if (filas.length === 0) {
        setError('El archivo está vacío o no pude leer ninguna hoja.');
        return;
      }
      if (filas.length > MAX_FILAS) {
        setError(`El archivo tiene ${filas.length} filas. El máximo son ${MAX_FILAS}.`);
        return;
      }

      // La primera fila es la cabecera salvo que parezca ya un dato.
      const cabecera = looksLikeHeader(filas[0]) ? filas[0] : [];
      const datos = cabecera.length > 0 ? filas.slice(1) : filas;
      if (cabecera.length === 0) {
        setError(
          'No encontré la fila de títulos. La primera fila debe decir qué hay en cada columna (Producto, Precio…).',
        );
        return;
      }

      setNombreFichero(file.name);
      setFilasLeidas(datos.filter((f) => f.some((c) => String(c ?? '').trim() !== '')).length);
      setFuente(modo === 'ingredientes'
        ? { datos, colsIng: matchIngredientHeaders(cabecera) }
        : { datos, colsRec: matchRecipeHeaders(cabecera) });
    } catch {
      setError(
        'No pude leer el archivo. Comprueba que es un Excel (.xlsx) o un CSV, y que no tiene contraseña.',
      );
    } finally {
      setLeyendo(false);
    }
  };

  /** Lo que se aprende al guardar: «Recordar que «crema acida» es Crema ácida». */
  const recordarAlias = async (items: Array<{ ingredientId: string; text: string }>) => {
    if (!items.length) return;
    try {
      await api.rememberIngredientAliases(items.map((i) => ({ ...i, source: 'excel' as const })));
      void qc.invalidateQueries({ queryKey: ['ingredients'] });
    } catch (e) {
      console.warn('[excel] no se pudo recordar el alias', e);
    }
  };

  const confirmarIngredientes = () => {
    if (!previaIng) return;
    const todos = [...previaIng.nuevos, ...previaIng.actualizados];
    const items = todos.map((d) => ({
      id: d.existingId,
      sourceRow: d.row,
      supplierName: d.supplierName,
      input: {
        name: d.name,
        brand: d.brand,
        presentation: d.presentation,
        group: d.group,
        purchaseQty: d.purchaseQty,
        purchaseUnit: d.purchaseUnit,
        netPrice: d.netPrice,
        pricePending: d.pricePending,
        vatPct: d.vatPct,
        densityGPerMl: d.densityGPerMl,
        active: true,
        createdVia: 'importacion' as const,
        priceSource: 'importacion' as const,
      },
    }));
    const alias = todos.filter((d) => d.aliasDe && d.existingId && !noRecordar[d.row]).map((d) => ({ ingredientId: d.existingId!, text: d.aliasDe! }));
    importIngredientes.mutate(
      { suppliers: previaIng.proveedoresNuevos, items },
      {
        onSuccess: async (r) => {
          setCerrando(true);
          try { await recordarAlias(alias); } finally { setCerrando(false); }
          if (r.errores.length > 0) {
            setErroresGuardado(r.errores);
            toast.notify(
              `Se importaron ${r.creados + r.actualizados}; ${r.errores.length} filas necesitan cambios. Revisa el detalle en esta ventana.`,
              'error',
            );
          } else {
            toast.success(`Importados: ${r.creados} nuevos y ${r.actualizados} actualizados.`);
            onClose();
          }
        },
        onError: (e) => {
          const detalle = e instanceof ApiError
            ? e.details
                .map((d) => `${d.path ? `${d.path}: ` : ''}${d.message ?? ''}`)
                .filter(Boolean)
            : [];
          setError(
            detalle.length > 0
              ? `No se pudo guardar: ${detalle.join(' · ')}`
              : `${e instanceof Error ? e.message : 'No se pudo guardar.'} Vuelve a descargar la plantilla o escríbenos con el nombre del archivo.`,
          );
        },
      },
    );
  };

  /**
   * «Precio de venta» de la plantilla: el plato entra en la carta con su
   * precio (con IVA, el de la cuenta). Si ya tenía precio en la carta, se
   * pone el del Excel. Un fallo aquí no deshace la receta: se avisa.
   */
  const ponerPreciosDeVenta = async (platos: RecipeDraft[]) => {
    if (!platos.length) return;
    try {
      const [recetas, carta] = await Promise.all([api.listRecipes(), api.listMenuItems()]);
      for (const plato of platos) {
        const receta = recetas.find((x) => x.kind === 'plato' && normalizeText(x.name) === normalizeText(plato.name));
        if (!receta || !plato.salePrice) continue;
        const enCarta = carta.find((m) => m.recipeId === receta.recipeId && !m.platformId);
        if (enCarta) {
          if (enCarta.sellingPriceGross === plato.salePrice) continue;
          await api.updateMenuItem(enCarta.menuItemId, {
            recipeId: enCarta.recipeId, name: enCarta.name, sellingPriceGross: plato.salePrice, vatPct: enCarta.vatPct,
            targetFoodCostPct: enCarta.targetFoodCostPct, platformId: enCarta.platformId, group: enCarta.group,
            channelPrices: enCarta.channelPrices, active: enCarta.active,
          });
        } else {
          await api.createMenuItem({
            recipeId: receta.recipeId, name: receta.name, sellingPriceGross: plato.salePrice,
            vatPct: settings.data?.vatPct ?? 16, targetFoodCostPct: 30, group: plato.group, active: true,
          });
        }
      }
    } catch (e) {
      toast.notify(`Las recetas se guardaron, pero no pude poner los precios de venta: ${e instanceof Error ? e.message : 'error'}. Ponlos desde Tu carta.`, 'error');
    } finally {
      void qc.invalidateQueries({ queryKey: ['menu-items'] });
      void qc.invalidateQueries({ queryKey: ['pricing'] });
    }
  };

  const confirmarRecetas = async () => {
    if (!previaRec) return;
    // Solo las recetas listas: todo emparejado y nada por contestar. Media
    // receta costeada engaña más que no tenerla.
    const completas = [...previaRec.nuevas, ...previaRec.actualizadas].filter(recipeDraftReady);
    const items = completas.map((r) => ({
      id: r.existingId,
      input: {
        name: r.name,
        code: r.code,
        kind: r.kind,
        group: r.group,
        steps: r.steps,
        components: r.components.map((c) => ({
          ref: c.ref!,
          block: c.block,
          qty: c.qty,
          unit: c.unit,
          wastePct: c.wastePct,
        })),
        yieldQty: r.yieldQty,
        yieldUnit: r.yieldUnit,
        portions: r.portions,
        safetyMarginPct: r.safetyMarginPct,
        // Lo dice el fichero (columna "Coste por rendimiento") o, si no viene,
        // el tipo: subreceta por rendimiento, plato por ración. Antes iba
        // `false` fijo y por eso las subrecetas importadas rompían a sus platos.
        useYieldCostAsBase: r.useYieldCostAsBase,
        active: true,
        createdVia: 'importacion' as const,
      },
    }));
    if (items.length === 0) {
      toast.notify('Ninguna receta está lista: arregla las que están por revisar.', 'error');
      return;
    }
    setError('');
    const usedNames = new Set(completas.flatMap((r) => r.components.flatMap((c) =>
      c.ref?.kind === 'ingredient_del_fichero' ? [c.ref.ingredientName] : [])));
    const stubs = (previaRec.ingredientStubs ?? []).filter((stub) => usedNames.has(stub.name));
    if (stubs.length) {
      try {
        const saved = await importIngredientes.mutateAsync({ items: stubs.map((input) => ({ input })) });
        // Los que sí se crearon ya existen: al recalcular la previa se emparejan
        // por nombre y un reintento no los duplica.
        if (saved.errores.length) {
          // Aquí no se guardó ninguna receta: el aviso de «las demás filas sí» mentía.
          setError(`No se guardó ninguna receta: no pude crear ${saved.errores.length === 1 ? 'un ingrediente nuevo' : `${saved.errores.length} ingredientes nuevos`} (${saved.errores.map((e) => `${e.name}: ${e.message}`).join(' · ')}). Corrígelo y vuelve a guardar.`);
          return;
        }
      } catch (error) {
        toast.fail(error);
        return;
      }
    }
    // «Recordar que «crema acida» es Crema ácida»: las líneas que el usuario emparejó a mano.
    const alias = completas.flatMap((r) => r.components.flatMap((c) => {
      const elegido = c.row != null ? corrRec.filas?.[c.row]?.ingredientId : undefined;
      return elegido && c.row != null && !noRecordar[c.row] ? [{ ingredientId: elegido, text: c.importedName }] : [];
    }));
    importRecetas.mutate(
      { items },
      {
        onSuccess: async (r) => {
          setCerrando(true);
          try {
            await recordarAlias(alias);
            await ponerPreciosDeVenta(completas.filter((d) => d.kind === 'plato' && d.salePrice));
          } finally { setCerrando(false); }
          toast.success(`Importadas: ${r.creados} nuevas y ${r.actualizados} actualizadas.`);
          onClose();
        },
        onError: (e) => toast.fail(e),
      },
    );
  };

  const guardando = importIngredientes.isPending || importRecetas.isPending || cerrando;

  // Cuantas filas entrarian si pulsase Importar ahora mismo. Si son cero, el
  // boton se apaga: mejor que dejarle pulsar y devolverle un aviso.
  const aImportar = previaIng
    ? previaIng.nuevos.length + previaIng.actualizados.length
    : previaRec
      ? [...previaRec.nuevas, ...previaRec.actualizadas].filter(recipeDraftReady).length
      : 0;

  const hayPrevia = Boolean(previaIng || previaRec);
  const porRevisar = previaIng
    ? previaIng.problemas.length
    : previaRec
      ? revisarRecetas(previaRec).length
      : 0;
  const cosa = modo === 'ingredientes' ? (aImportar === 1 ? 'ingrediente' : 'ingredientes') : (aImportar === 1 ? 'receta' : 'recetas');
  const inputId = `excel-fichero-${modo}`;
  const subir = (texto: string, principal: boolean) => (
    <label htmlFor={inputId} className={`btn btn--sm${principal ? '' : ' btn--ghost'}`}>{leyendo ? 'Leyendo…' : texto}</label>
  );
  /** Las plantillas de Alex, tal cual (con sus colores, desplegables y ejemplos). */
  const descargarPlantilla = () => descargarArchivoPublico(PLANTILLA_ARCHIVOS[modo]);
  /** «Ver un ejemplo»: la misma plantilla ya llena, con datos de un restaurante en pesos. */
  const verEjemplo = () => descargarArchivoPublico(modo === 'ingredientes' ? INGREDIENT_EXAMPLE_FILE : RECIPE_EXAMPLE_FILE);
  const ejemplo = <button type="button" className="ing-enlace" onClick={verEjemplo}>Ver un ejemplo</button>;

  const corregirIng = (row: number, patch: IngredientRowCorrection) => setCorrIng((prev) => ({ ...prev, [row]: { ...prev[row], ...patch } }));
  const corregirRec = (row: number, patch: RecipeRowCorrection) => setCorrRec((prev) => ({ ...prev, filas: { ...prev.filas, [row]: { ...prev.filas?.[row], ...patch } } }));
  const elegirTipo = (recipeName: string, kind: 'plato' | 'subreceta') => setCorrRec((prev) => ({ ...prev, tipos: { ...prev.tipos, [normalizaReceta(recipeName)]: kind } }));
  const recordar = { valor: (row: number) => !noRecordar[row], cambiar: (row: number, si: boolean) => setNoRecordar((prev) => ({ ...prev, [row]: !si })) };

  return (
    <Drawer
      open
      title={hayPrevia ? 'Antes de guardar' : modo === 'ingredientes' ? 'Excel' : 'Excel de recetas'}
      subtitle={hayPrevia
        ? `${nombreFichero} · ${filasLeidas} ${filasLeidas === 1 ? 'fila' : 'filas'}`
        : modo === 'ingredientes'
          ? 'Para cuando tienes muchos ingredientes a la vez. ¿Qué quieres hacer?'
          : 'Para pasar muchas recetas a la vez: platos y preparaciones en el mismo archivo. ¿Dónde las tienes hoy?'}
      onClose={onClose}
      width="36rem"
      footer={
        erroresGuardado.length > 0 ? (
          <button type="button" className="btn" onClick={onClose}>
            Entendido
          </button>
        ) : hayPrevia ? (
          <div className="ing-excel__pie">
            <p>
              {aImportar === 0
                ? 'Todavía no hay nada listo para guardar.'
                : `Se ${aImportar === 1 ? 'guarda' : 'guardan'} ${modo === 'ingredientes' ? (aImportar === 1 ? 'el' : 'los') : aImportar === 1 ? 'la' : 'las'} ${aImportar} que ${aImportar === 1 ? 'está' : 'están'} bien.`}
              {porRevisar > 0 && (modo === 'ingredientes'
                ? (porRevisar === 1 ? ' El que queda por revisar sigue aparte hasta que lo arregles.' : ` Los ${porRevisar} por revisar quedan aparte hasta que los arregles.`)
                : (porRevisar === 1 ? ' La que queda por revisar sigue aparte hasta que la arregles.' : ` Las ${porRevisar} por revisar quedan aparte hasta que las arregles.`))}
            </p>
            <div className="ing-excel__botones">
              <button
                type="button"
                className="btn"
                disabled={guardando || aImportar === 0}
                onClick={modo === 'ingredientes' ? confirmarIngredientes : () => void confirmarRecetas()}
              >
                {guardando ? 'Guardando…' : aImportar === 0 ? 'Nada que guardar' : `Guardar ${aImportar} ${cosa}`}
              </button>
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                Cancelar
              </button>
            </div>
          </div>
        ) : undefined
      }
    >
      <div className="stack ing-excel">
        <input
          id={inputId}
          type="file"
          accept=".csv,.xlsx,.xls,text/csv"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void cargar(file);
            e.target.value = '';
          }}
        />

        {error && <p className="alerta alerta--error">{error}</p>}

        {erroresGuardado.length > 0 && (
          <div className="alerta alerta--error">
            <strong>Hay {erroresGuardado.length} filas que debes corregir en el Excel.</strong>
            <p>Las demás filas sí se guardaron. Corrige estas y vuelve a subir el archivo:</p>
            <ul className="lista-problemas">
              {erroresGuardado.slice(0, 20).map((item, index) => (
                <li key={`${item.row ?? item.name}-${index}`}>
                  {item.row && <strong>Fila {item.row} · </strong>}
                  <strong>{item.name}:</strong> {item.message}.
                </li>
              ))}
            </ul>
            {erroresGuardado.length > 20 && (
              <p>Y {erroresGuardado.length - 20} filas más.</p>
            )}
          </div>
        )}

        {/* Paso 1: elegir el camino. Cada uno con sus tres pasos. */}
        {!hayPrevia && modo === 'ingredientes' && (
          <>
            <section className={`ing-camino${destacar === 'lista' ? ' is-destacado' : ''}`} aria-labelledby="camino-lista">
              <h3 id="camino-lista">Pasar mi lista de ingredientes</h3>
              <p className="ing-camino__sub">Tienes tus productos en un Excel y quieres cargarlos en Alexia.</p>
              <ol className="ing-pasos">
                <li><p>Descarga la plantilla.</p><button type="button" className="btn btn--ghost btn--sm" onClick={descargarPlantilla}>Descargar plantilla</button></li>
                <li><p>Llénala, una fila por producto. Solo hace falta el <strong>nombre</strong> y <strong>en qué se mide</strong> (kg, L, pz…). El precio, si lo tienes: si es con IVA, pon «Sí» en «¿Con IVA?». {ejemplo}</p></li>
                <li><p>Súbela. Antes de guardar verás lo que va a entrar.</p>{subir('Subir mi Excel', destacar === 'lista')}</li>
              </ol>
            </section>
            <section className={`ing-camino${destacar === 'precios' ? ' is-destacado' : ''}`} aria-labelledby="camino-precios">
              <h3 id="camino-precios">Cambiar precios de los que ya tengo</h3>
              <p className="ing-camino__sub">Para cuando suben los precios y quieres cambiarlos todos de una vez.</p>
              <ol className="ing-pasos">
                <li><p>Descarga tu lista de ingredientes, tal como está hoy.</p>{onDescargarLista && <button type="button" className="btn btn--ghost btn--sm" onClick={onDescargarLista}>Descargar mi lista</button>}</li>
                <li><p>Cambia los precios, sin IVA. <strong>No borres la primera columna:</strong> así Alexia sabe qué ingrediente es cada fila.</p></li>
                <li><p>Súbela. Verás qué precios cambian antes de guardar.</p>{subir('Subir mi lista', destacar === 'precios')}</li>
              </ol>
            </section>
            {onSubirFactura && (
              <p className="ing-excel__nota">
                ¿Tienes las facturas a mano? Suele ser más rápido <button type="button" className="ing-enlace" onClick={onSubirFactura}>subir una factura</button>: Alexia crea los ingredientes y pone los precios por ti.
              </p>
            )}
          </>
        )}
        {!hayPrevia && modo === 'recetas' && (
          <>
            <section className="ing-camino is-destacado" aria-labelledby="camino-recetas">
              <h3 id="camino-recetas">En mi propio Excel, en una libreta o en ningún lado</h3>
              <p className="ing-camino__sub">Usa nuestra plantilla: una sola hoja para todo.</p>
              <ol className="ing-pasos">
                <li><p>Descarga la plantilla de recetas.</p><button type="button" className="btn btn--ghost btn--sm" onClick={descargarPlantilla}>Descargar plantilla</button></li>
                <li><p>Llénala. <strong>Una fila por ingrediente.</strong> El nombre del plato va solo en su primera fila; las de abajo sin nombre son del mismo. Si un plato usa una preparación (la masa, una salsa), escríbela en el mismo archivo, arriba o abajo. {ejemplo}</p></li>
                <li><p>Súbela. Antes de guardar verás lo que va a entrar.</p>{subir('Subir mi Excel', true)}</li>
              </ol>
            </section>
            <p className="ing-excel__nota">¿Son pocas? Suele ser más rápido <Link to="/alexia" onClick={onClose}>dictarlas una por una</Link>.</p>
            {onDescargarFichas && (
              <section className="ing-camino" aria-labelledby="camino-fichas">
                <h3 id="camino-fichas">Descargar todas las fichas (PDF)</h3>
                <p className="ing-camino__sub">Las fichas técnicas de tus platos y preparaciones, en un solo PDF para la cocina.</p>
                <button type="button" className="btn btn--ghost btn--sm" onClick={onDescargarFichas}>Descargar todas las fichas (PDF)</button>
              </section>
            )}
          </>
        )}
        {/* Sin «¿Qué vas a pasar?» (revisión del 12 oct 2026): cada pantalla ya sabe qué sube. */}

        {/* Paso 2: antes de guardar */}
        {plantillaIng && plantillaIng.conIva > 0 && (
          <div className="ing-excel__aviso ing-excel__iva">
            <strong>{plantillaIng.conIva === 1 ? '1 precio venía con IVA' : `${plantillaIng.conIva} precios venían con IVA`}</strong>
            <p>Alexia guarda los precios sin IVA. ¿Qué IVA les quito?</p>
            <label className="ing-excel__iva-campo">
              <input
                className="input"
                inputMode="decimal"
                aria-label="IVA de los precios con IVA"
                value={ivaTecleado ?? String(ivaPlantilla)}
                onChange={(e) => setIvaTecleado(e.target.value)}
              />
              <span>%</span>
            </label>
          </div>
        )}
        {previaIng && <PreviaIngredientes previa={previaIng} corregir={corregirIng} recordar={recordar} />}
        {previaRec && (
          <PreviaRecetas
            previa={previaRec}
            corregir={corregirRec}
            elegirTipo={elegirTipo}
            recordar={recordar}
            noEsPlantilla={(
              <div className="ing-excel__botones">
                <button type="button" className="btn btn--sm" onClick={descargarPlantilla}>Descargar plantilla</button>
                {subir('Subir otro', false)}
              </div>
            )}
          />
        )}
        {hayPrevia && !(previaRec && previaRec.columnasNoEncontradas.length > 0) && (
          <p className="ing-excel__nota">
            ¿No es el archivo? {subir('Subir otro', false)}
          </p>
        )}
      </div>
    </Drawer>
  );
}

const normalizaReceta = (nombre: string): string =>
  nombre.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

function Resumen({ items }: { items: Array<{ etiqueta: string; valor: number; tono?: 'bien' | 'atencion' | 'neutro' }> }) {
  return (
    <div className="ing-contadores">
      {items.map((i) => (
        <article key={i.etiqueta} className={`stat ing-contador ing-contador--${i.tono ?? 'neutro'}`}>
          <p className="stat__value">{i.valor}</p>
          <p className="stat__label">{i.etiqueta}</p>
        </article>
      ))}
    </div>
  );
}

interface ItemRevisar {
  clave: string;
  titulo: string;
  texto: ReactNode;
  fila?: number;
  arreglo?: ReactNode;
}

/** «Revisa estas N»: lo que no entra, en una frase y con su arreglo al lado. */
function PorRevisar({ items, total }: { items: ItemRevisar[]; total: number }) {
  if (items.length === 0) return null;
  const todasAqui = items.every((i) => i.arreglo);
  return (
    <section className="ing-revisar" aria-labelledby="excel-revisar">
      <h3 id="excel-revisar">Revisa {total === 1 ? 'esta' : `estas ${total}`}</h3>
      <p className="ing-revisar__sub">
        {todasAqui
          ? `${total === 1 ? 'Arréglala' : 'Arréglalas'} aquí mismo, sin volver al Excel.`
          : 'Arregla aquí lo que puedas; lo demás, en el Excel. Lo que está bien se puede guardar ya.'}
      </p>
      {items.map((i) => (
        <article key={i.clave} className="ing-revisar__fila">
          <div className="ing-revisar__cabeza"><strong>{i.titulo}</strong>{i.fila != null && i.fila > 0 && <span>fila {i.fila}</span>}</div>
          <p>{i.texto}</p>
          {i.arreglo}
        </article>
      ))}
    </section>
  );
}

/** «Escribirlo yo»: un campo pequeño con su botón, que se abre al tocarlo. */
function Escribirlo({ etiqueta, alUsar, sufijo, abierto = false }: { etiqueta: string; alUsar: (valor: number) => void; sufijo?: string; abierto?: boolean }) {
  const [abrir, setAbrir] = useState(abierto);
  const [valor, setValor] = useState('');
  if (!abrir) return <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAbrir(true)}>Escribirlo yo</button>;
  const numero = parseDecimal(valor, 0);
  return (
    <>
      <input className="input" inputMode="decimal" aria-label={etiqueta} value={valor} onChange={(e) => setValor(e.target.value)} placeholder={sufijo ? `0 ${sufijo}` : '0.00'} />
      <button type="button" className="btn btn--sm" disabled={!(numero > 0)} onClick={() => alUsar(numero)}>Usar</button>
    </>
  );
}

/** «Recordar que «crema acida» es Crema ácida»: guarda un alias al guardar. */
function Recordar({ row, de, es, recordar }: { row: number; de: string; es: string; recordar: RecordarProps }) {
  return (
    <label className="ing-revisar__recordar">
      <input type="checkbox" checked={recordar.valor(row)} onChange={(e) => recordar.cambiar(row, e.target.checked)} />
      Recordar que «{de}» es {es}
    </label>
  );
}

interface RecordarProps { valor: (row: number) => boolean; cambiar: (row: number, si: boolean) => void }

/** Cómo lo compra hoy: «$13.70 el saco de 20 kg». */
function comoLoCompra(e: { netPrice?: number; purchaseQty?: number; purchaseUnit?: Unit; presentation?: string }): string {
  if (e.netPrice == null || !e.purchaseUnit) return '';
  const formato = `${formatNumber(e.purchaseQty ?? 1)} ${unitLabel(e.purchaseUnit)}`;
  return `${formatMoney(e.netPrice)} ${e.presentation ? `el ${e.presentation} de ${formato}` : `por ${formato}`}`;
}

/**
 * Tabla plegada con lo que está bien. El resumen dice "4"; esto dice CUALES,
 * que es lo unico que permite pillar a tiempo una columna mal emparejada. Se
 * limita a 50 filas: mas no se leen, y el resumen ya da el total.
 */
function TablaPrevia({
  titulo,
  cabeceras,
  filas,
  total,
}: {
  titulo: string;
  cabeceras: string[];
  filas: Array<{ clave: string; celdas: ReactNode[] }>;
  total: number;
}): JSX.Element | null {
  if (filas.length === 0) return null;
  const visibles = filas.slice(0, 50);
  return (
    <details className="ing-bien">
      <summary><span className="ing-ficha__chevron" aria-hidden="true">›</span> {titulo}</summary>
      <div className="table-wrap">
        <table className="table table--compact">
          <thead>
            <tr>
              {cabeceras.map((c, i) => (
                <th key={i}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibles.map((f, i) => (
              <tr key={`${f.clave}-${i}`}>
                {f.celdas.map((c, j) => (
                  <td key={j}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > visibles.length && (
        <p className="field__hint">Y {total - visibles.length} más que no caben aquí.</p>
      )}
    </details>
  );
}

const UNIDADES_CHIP: Record<string, string> = { kg: 'kg', g: 'g', l: 'L', ml: 'ml', pz: 'pieza' };

function PreviaIngredientes({ previa, corregir, recordar }: { previa: IngredientImportResult; corregir: (row: number, patch: IngredientRowCorrection) => void; recordar: RecordarProps }): JSX.Element {
  if (previa.columnasNoEncontradas.length > 0) {
    const nombres: Record<string, string> = {
      name: 'Producto',
      purchaseQty: 'Cantidad de compra',
      purchaseUnit: 'Unidad de compra',
      netPrice: 'Precio sin IVA (o Precio con IVA)',
    };
    return (
      <p className="alerta alerta--error">
        No encontré estas columnas:{' '}
        <strong>{previa.columnasNoEncontradas.map((c) => nombres[c] ?? c).join(', ')}</strong>.
        Descarga la plantilla para ver los títulos que espero.
      </p>
    );
  }

  const arreglo = (p: ImportIssue): { texto: ReactNode; arreglo?: ReactNode } => {
    const fix = p.fix;
    if (!fix) return { texto: p.message };
    switch (fix.kind) {
      case 'cuantos':
        // «1.500 kg»: ¿1500 o 1,5? Se pregunta (revisión del 14 oct 2026).
        return { texto: p.message, arreglo: (
          <div className="ing-revisar__arreglo" role="group" aria-label="Cantidad">
            {fix.opciones.map((n) => (
              <button key={n} type="button" className="chip" onClick={() => corregir(p.row, { purchaseQty: n })}>{formatQty(n)}</button>
            ))}
          </div>
        ) };
      case 'precio':
        return fix.value != null
          ? { texto: <>{p.message} ¿Es {formatMoney(fix.value)}?</>, arreglo: (
            <div className="ing-revisar__arreglo">
              <button type="button" className="btn btn--sm" onClick={() => corregir(p.row, { precio: fix.value })}>Sí, {formatMoney(fix.value)}</button>
              <Escribirlo etiqueta={`Precio de ${p.name ?? 'la fila'}`} alUsar={(v) => corregir(p.row, { precio: v })} />
            </div>
          ) }
          : { texto: <>{p.message} Escríbelo aquí, sin IVA.</>, arreglo: (
            <div className="ing-revisar__arreglo"><Escribirlo abierto etiqueta={`Precio de ${p.name ?? 'la fila'}`} alUsar={(v) => corregir(p.row, { precio: v })} /></div>
          ) };
      case 'unidad':
        return { texto: p.message, arreglo: (
          <div className="ing-revisar__arreglo" role="group" aria-label={`En qué se mide ${p.name ?? ''}`}>
            {fix.options.map((u) => (
              <button key={u} type="button" className="chip" onClick={() => corregir(p.row, { purchaseUnit: u as 'kg' | 'l' | 'pz' })}>{UNIDADES_CHIP[u] ?? unitLabel(u)}</button>
            ))}
          </div>
        ) };
      case 'existe': {
        const hoy = comoLoCompra(fix.existing);
        const enExcel = fix.newPrice != null ? `${formatMoney(fix.newPrice)} por ${formatNumber(fix.newQty)} ${unitLabel(fix.newUnit)}` : '';
        return { texto: <>Ya tienes «{fix.existingName}»{hoy ? ` (${hoy})` : ''}.{enExcel ? ` En el Excel viene a ${enExcel}.` : ''}</>, arreglo: (
          <div className="ing-revisar__arreglo">
            <button type="button" className="btn btn--sm" onClick={() => corregir(p.row, { existente: 'actualizar' })}>Actualizar su precio</button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => corregir(p.row, { existente: 'crear' })}>Es otra, créala</button>
          </div>
        ) };
      }
      case 'parecido': {
        const c = fix.candidates[0];
        return { texto: <>{p.message} ¿Es tu <strong>{c.name}</strong>?</>, arreglo: (
          <>
            <div className="ing-revisar__arreglo">
              <button type="button" className="btn btn--sm" onClick={() => corregir(p.row, { parecido: c.ingredientId })}>Sí, es esa</button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => corregir(p.row, { parecido: 'crear' })}>Es otra, créala</button>
            </div>
            <Recordar row={p.row} de={fix.raw} es={c.name} recordar={recordar} />
          </>
        ) };
      }
      default:
        return { texto: p.message };
    }
  };

  const bien = previa.nuevos.length + previa.actualizados.length;
  return (
    <div className="stack">
      <Resumen
        items={[
          { etiqueta: previa.nuevos.length === 1 ? 'nuevo' : 'nuevos', valor: previa.nuevos.length, tono: 'bien' },
          { etiqueta: 'ya los tenías, se actualizan', valor: previa.actualizados.length },
          { etiqueta: 'por revisar', valor: previa.problemas.length, tono: previa.problemas.length > 0 ? 'atencion' : 'neutro' },
        ]}
      />
      {previa.proveedoresNuevos.length > 0 && (
        <p className="field__hint">
          Se darán de alta {previa.proveedoresNuevos.length === 1 ? 'este proveedor' : `estos ${previa.proveedoresNuevos.length} proveedores`}: {previa.proveedoresNuevos.join(', ')}.
        </p>
      )}
      <PorRevisar
        total={previa.problemas.length}
        items={previa.problemas.slice(0, 30).map((p, i) => ({ clave: `${p.row}-${i}`, titulo: p.name ?? (p.row > 0 ? `Fila ${p.row}` : 'Del archivo'), fila: p.name ? p.row : undefined, ...arreglo(p) }))}
      />
      <TablaPrevia
        titulo={`Ver ${bien === 1 ? 'el que está bien' : `los ${bien} que están bien`}`}
        cabeceras={['Producto', 'Grupo', 'Compra', 'Precio sin IVA', 'Proveedor', '']}
        total={bien}
        filas={[
          ...previa.nuevos.map((d) => ({ d, nuevo: true })),
          ...previa.actualizados.map((d) => ({ d, nuevo: false })),
        ].map(({ d, nuevo }) => ({
          clave: `${d.name}|${d.brand ?? ''}`,
          celdas: [
            <>
              {d.name}
              {d.brand && <span className="cell-sub">{d.brand}</span>}
            </>,
            ingredientGroupLabel(d.group),
            `${formatNumber(d.purchaseQty)} ${d.purchaseUnit}`,
            d.netPrice == null ? 'Sin precio · pendiente' : formatMoney(d.netPrice),
            d.supplierName ?? '—',
            <span className={`badge badge--${nuevo ? 'bien' : 'atencion'}`}>
              {nuevo ? 'Nuevo' : 'Actualiza'}
            </span>,
          ],
        }))}
      />
    </div>
  );
}

/** Lo que queda por revisar en un Excel de recetas: los arreglos y lo que no se encontró. */
function revisarRecetas(previa: RecipeImportResult): Array<{ problema?: ImportIssue; receta?: string; faltan?: string[] }> {
  const todas = [...previa.nuevas, ...previa.actualizadas];
  // Recetas con algo sin emparejar y SIN arreglo propuesto (lo que se busca en Preparaciones y no está).
  const sinArreglo = todas
    .map((r) => ({ receta: r.name, faltan: r.components.filter((c) => !c.ref && !c.fix).map((c) => c.importedName) }))
    .filter((r) => r.faltan.length > 0);
  const problemas = [...previa.problemas].sort((a, b) => a.row - b.row);
  return [...problemas.map((problema) => ({ problema })), ...sinArreglo];
}

/** «500 g», «1 kg», «2 L», «400 piezas». */
function rindeLegible(qty: number, unit: Unit): string {
  if (UNITS[unit]?.dimension === 'count') {
    const n = Math.ceil(qty * (UNITS[unit]?.factor ?? 1) - 1e-9);
    return `${formatNumber(n)} ${n === 1 ? 'pieza' : 'piezas'}`;
  }
  return cantidadCocina(qty, unit);
}

/**
 * De dónde sale cada preparación que usa la receta, como en el diseño:
 * «usa Salsa brava, de este mismo archivo ✓» o «usa tu Sofrito base».
 */
function usaPreparaciones(r: RecipeDraft, delArchivo: readonly RecipeDraft[], guardadas: readonly Recipe[]): ReactNode {
  const enArchivo = new Set(delArchivo.map((d) => normalizeText(d.name)));
  const idsDelArchivo = new Set(delArchivo.flatMap((d) => (d.existingId ? [d.existingId] : [])));
  const partes: ReactNode[] = [];
  const vistos = new Set<string>();
  for (const c of r.components) {
    const ref = c.ref;
    if (!ref || (ref.kind !== 'recipe' && ref.kind !== 'recipe_del_fichero')) continue;
    if (ref.kind === 'recipe_del_fichero' || (ref.kind === 'recipe' && idsDelArchivo.has(ref.recipeId))) {
      const nombre = ref.kind === 'recipe_del_fichero'
        ? ref.recipeName
        : delArchivo.find((d) => d.existingId === ref.recipeId)?.name ?? c.importedName;
      if (vistos.has(nombre)) continue;
      vistos.add(nombre);
      partes.push(<span key={nombre}>usa {nombre}, de este mismo archivo <span className="ing-excel__ok" aria-label="encontrada">✓</span></span>);
    } else {
      const guardada = guardadas.find((g) => g.recipeId === ref.recipeId);
      const nombre = guardada?.name ?? c.importedName;
      if (vistos.has(nombre) || enArchivo.has(normalizeText(nombre))) continue;
      vistos.add(nombre);
      partes.push(<span key={nombre}>usa tu {nombre}</span>);
    }
  }
  return partes.length ? partes.reduce<ReactNode[]>((acc, p, i) => (i ? [...acc, ' · ', p] : [p]), []) : null;
}

function PreviaRecetas({ previa, corregir, elegirTipo, recordar, noEsPlantilla }: {
  previa: RecipeImportResult;
  /** «Descargar plantilla» + «Subir otro», para cuando el archivo no es la plantilla. */
  noEsPlantilla: ReactNode;
  corregir: (row: number, patch: RecipeRowCorrection) => void;
  elegirTipo: (recipeName: string, kind: 'plato' | 'subreceta') => void;
  recordar: RecordarProps;
}): JSX.Element {
  const ingredientes = useIngredients().data ?? [];
  const recetasGuardadas = useRecipes().data ?? [];
  const [eligiendo, setEligiendo] = useState<Record<number, boolean>>({});
  if (previa.columnasNoEncontradas.length > 0) {
    // Revisión de Alex (12 oct): no listar columnas; decir qué hacer, con los dos botones ahí mismo.
    return (
      <div className="alerta alerta--error">
        <p>Este archivo no tiene la forma de nuestra plantilla. Descárgala y pasa tus recetas ahí.</p>
        {noEsPlantilla}
      </div>
    );
  }

  const todas = [...previa.nuevas, ...previa.actualizadas];
  const completas = todas.filter(recipeDraftReady);
  const platos = completas.filter((r) => r.kind === 'plato').length;
  const stubs = previa.ingredientStubs ?? [];
  // Separado por DÓNDE lo hemos buscado: decir «ingrediente no encontrado» de
  // una línea que es una subreceta manda al usuario al sitio equivocado
  // (bug que encontró Alex el 1 de septiembre).
  const donde = new Map(previa.sinEmparejar.map((c) => [c.name, c.buscadoEn]));
  const motivo = (nombre: string) => {
    const d = donde.get(nombre);
    return d === 'receta'
      ? `«${nombre}» no está entre tus preparaciones (la busqué ahí porque la línea dice «preparación»). Revisa que esté escrita igual o agrégala a este mismo archivo.`
      : d === 'cualquiera'
        ? `«${nombre}» no está ni entre tus ingredientes ni entre tus preparaciones.`
        : `«${nombre}» no está en tus ingredientes. Revisa que esté escrito igual.`;
  };
  const opciones = ingredientes.filter((i) => i.active).map((i) => ({ value: i.ingredientId, label: i.name })).sort((a, b) => a.label.localeCompare(b.label, 'es'));

  const arreglo = (p: ImportIssue): { texto: ReactNode; arreglo?: ReactNode } => {
    const fix: ImportFix | undefined = p.fix;
    if (!fix) return { texto: p.message };
    switch (fix.kind) {
      case 'cuantos':
        return { texto: p.message, arreglo: (
          <div className="ing-revisar__arreglo" role="group" aria-label="Cantidad">
            {fix.opciones.map((n) => (
              <button key={n} type="button" className="chip" onClick={() => corregir(p.row, { qty: n })}>{formatQty(n)}</button>
            ))}
          </div>
        ) };
      case 'tipo':
        return { texto: p.message, arreglo: (
          <div className="ing-revisar__arreglo">
            <button type="button" className="btn btn--sm" onClick={() => elegirTipo(fix.recipeName, 'subreceta')}>Sí, es una preparación</button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => elegirTipo(fix.recipeName, 'plato')}>Es un plato de mi carta</button>
          </div>
        ) };
      case 'cantidad':
        return { texto: <>{p.message} ¿Son {formatNumber(fix.qty)} {unitLabel(fix.unit)}?</>, arreglo: (
          <div className="ing-revisar__arreglo">
            <button type="button" className="btn btn--sm" onClick={() => corregir(p.row, { unit: fix.unit })}>Sí, {formatNumber(fix.qty)} {unitLabel(fix.unit)}</button>
            {(['g', 'kg', 'ml', 'l', 'pz'] as Unit[]).filter((u) => u !== fix.unit && (u === 'pz' || dimensionOf(u) === dimensionOf(fix.unit))).map((u) => (
              <button key={u} type="button" className="chip" onClick={() => corregir(p.row, { unit: u })}>{formatNumber(fix.qty)} {UNIDADES_CHIP[u] === 'pieza' ? 'piezas' : UNIDADES_CHIP[u]}</button>
            ))}
          </div>
        ) };
      case 'unidad':
        return { texto: p.message, arreglo: (
          <div className="ing-revisar__arreglo" role="group" aria-label="Unidad">
            {fix.options.map((u) => (
              <button key={u} type="button" className="chip" onClick={() => corregir(p.row, { unit: u })}>{UNIDADES_CHIP[u] ?? unitLabel(u)}</button>
            ))}
          </div>
        ) };
      case 'parecido': {
        const [primero, ...otros] = fix.candidates;
        const elegido = eligiendo[p.row];
        return { texto: <>{p.message} ¿Es tu <strong>{primero?.name}</strong>{otros.length ? <> o {otros.map((o) => o.name).join(' o ')}</> : null}?</>, arreglo: (
          <>
            <div className="ing-revisar__arreglo">
              {fix.candidates.map((c, i) => (
                <button key={c.ingredientId} type="button" className={`btn btn--sm${i > 0 ? ' btn--ghost' : ''}`} onClick={() => corregir(p.row, { ingredientId: c.ingredientId })}>
                  {fix.candidates.length === 1 ? 'Sí, es esa' : c.name}
                </button>
              ))}
              {!elegido && <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEligiendo((prev) => ({ ...prev, [p.row]: true }))}>Elegir otra</button>}
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => corregir(p.row, { crear: true })}>Crearla nueva</button>
            </div>
            {elegido && (
              <div className="ing-revisar__arreglo">
                <SmartSelect
                  ariaLabel={`Ingrediente para «${fix.raw}»`}
                  value=""
                  buscable
                  placeholder="Busca en tus ingredientes…"
                  onChange={(value) => value && corregir(p.row, { ingredientId: value })}
                  options={[{ value: '', label: 'Elige…' }, ...opciones]}
                />
              </div>
            )}
            {primero && <Recordar row={p.row} de={fix.raw} es={fix.candidates.length === 1 ? primero.name : 'el que elijas'} recordar={recordar} />}
          </>
        ) };
      }
      default:
        return { texto: p.message };
    }
  };

  const revisar: ItemRevisar[] = revisarRecetas(previa).slice(0, 30).map((r, i) => {
    if (r.problema) {
      const p = r.problema;
      return { clave: `p-${p.row}-${i}`, titulo: p.name ?? (p.row > 0 ? `Fila ${p.row}` : 'Del archivo'), fila: p.name ? p.row : undefined, ...arreglo(p) };
    }
    const faltan = r.faltan ?? [];
    return { clave: `r-${r.receta}`, titulo: r.receta ?? '', texto: faltan.length === 1 ? motivo(faltan[0]) : <>Le faltan {faltan.length}: {faltan.map((f) => `«${f}»`).join(', ')}. Revisa que estén escritos igual.</> };
  });
  const totalRevisar = revisarRecetas(previa).length;

  return (
    <div className="stack">
      <Resumen
        items={[
          { etiqueta: 'platos para tu carta', valor: platos, tono: 'bien' },
          { etiqueta: 'preparaciones', valor: completas.length - platos, tono: 'bien' },
          { etiqueta: 'por revisar', valor: totalRevisar, tono: totalRevisar > 0 ? 'atencion' : 'neutro' },
        ]}
      />
      <PorRevisar total={totalRevisar} items={revisar} />
      {stubs.length > 0 && (
        <div className="ing-excel__aviso">
          <strong>{stubs.length === 1 ? '1 ingrediente no lo tienes todavía' : `${stubs.length} ingredientes no los tienes todavía`}</strong>
          <p>Se crean «por confirmar», sin precio. Los platos que los usan quedan «Sin terminar» hasta que les pongas precio (o subas la factura).</p>
          <details>
            <summary>Ver cuáles</summary>
            <p>{stubs.map((st) => st.name).join(', ')}</p>
          </details>
        </div>
      )}
      <TablaPrevia
        titulo={`Ver ${completas.length === 1 ? 'la que está bien' : `las ${completas.length} que están bien`}`}
        cabeceras={['Receta', 'Tipo', 'Rinde', 'Usa', '']}
        total={completas.length}
        filas={completas.map((r) => ({
          clave: r.name,
          celdas: [
            r.name,
            RECIPE_KIND_LABELS[r.kind],
            // Revisión del 14 oct 2026: las preparaciones dicen lo que rinden («rinde 500 g»), no «Porciones 1».
            r.kind === 'subreceta' ? `rinde ${rindeLegible(r.yieldQty, r.yieldUnit)}` : `${formatNumber(r.portions || 1)} ${(r.portions || 1) === 1 ? 'porción' : 'porciones'}`,
            usaPreparaciones(r, todas, recetasGuardadas) || <span className="muted">—</span>,
            <span className={`badge badge--${r.existingId ? 'atencion' : 'bien'}`}>
              {r.existingId ? 'Actualiza' : 'Nueva'}
            </span>,
          ],
        }))}
      />
    </div>
  );
}
